import Head from "next/head";
import Link from "next/link";

import { constants } from "src/shared/constants";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

// Suns Up Ticket Pass FAQ — copy provided by the Suns.
export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "What games are included with the Suns Up Season Ticket Pass?",
    answer:
      "The Suns Up Season Ticket Pass provides access to tickets for every game this season. Pass-holders may reserve up to four (4) games at a time, with reservations available on a first-come, first-served basis.",
  },
  {
    question: "How do I manage my Suns Up Season Pass reservations?",
    answer:
      'Starting on October 6, you can navigate to Upcoming Games and select "Reserve" next to the game you want to attend. Please note that you can hold and manage up to four (4) reservations at a time.',
  },
  {
    question: "What if I can't attend a game I reserve?",
    answer:
      "If you are unable to attend a reserved game, you must cancel at least 48 hours in advance.",
  },
  {
    question: "How many passes can I purchase?",
    answer: "You may purchase up to four passes per account.",
  },
  {
    question: "Can I receive a refund for unused tickets?",
    answer: "No. Passes are non-transferable and non-refundable.",
  },
];

// Terms & Conditions reused from phoenixsuns_pass.
export const TERMS_AND_CONDITION: TTeamConfig["LANGUAGES"]["TERMS_AND_CONDITION"] =
  () => (
    <>
      By purchasing you agree to receive SMS alerts regarding ticket delivery
      and agree to the{" "}
      <Link target={"_blank"} href="https://www.attend.tech/terms-of-use">
        Terms of Use
      </Link>{" "}
      and{" "}
      <Link target={"_blank"} href="https://www.attend.tech/privacy-policy">
        Privacy Policy
      </Link>
    </>
  );

export const REGISTER_TERMS_AND_CONDITION = () => (
  <>
    I provide my prior express written consent for Suns Legacy Partners, L.L.C.
    d/b/a Phoenix Suns (&quot;Suns&quot;), Phoenix Mercury Basketball, LLC d/b/a
    Phoenix Mercury (&quot;Mercury&quot;), NAZB, LLC d/b/a Valley Suns
    (&quot;Valley Suns&quot;), and Phoenix Arena Development Limited Partnership
    d/b/a Mortgage Matchup Center (&quot;Arena,&quot; and together with Suns,
    Mercury, and Valley Suns, the &quot;Team&quot;) to contact me at the
    telephone number(s) I have provided, including through the use of an
    automatic telephone dialing system, artificial or prerecorded voice
    messages, and/or text messages (SMS or MMS), for marketing and promotional
    purposes, including updates and offers related to the Team&apos;s products,
    services, and events. I also consent to receive email communications from
    the Team for these purposes. I understand and agree that my consent is not a
    condition of purchasing any property, goods, or services from the Team, and
    that I am not required to provide this consent to make any purchase. Message
    and data rates may apply. Message frequency varies. To opt-out of Text
    Message Services, text &quot;STOP,&quot; &quot;STOP ALL,&quot;
    &quot;QUIT,&quot; &quot;CANCEL,&quot; &quot;UNSUBSCRIBE,&quot; or
    &quot;END&quot; from the device which you used to opt-in. To opt out of
    emails, I may click the &quot;unsubscribe&quot; link in any email I receive
    from the Team. I may also contact the Team directly to revoke consent. I
    understand that my information will be used as described herein and in
    compliance with the{" "}
    <Link href="https://www.nba.com/privacy-policy" target="_blank">
      NBA privacy policy
    </Link>{" "}
    and{" "}
    <Link
      href="https://mortgagematchupcenter.com/privacy-policy/"
      target="_blank"
    >
      Mortgage Matchup Center Privacy Policy
    </Link>
    . You also agree to the platform&apos;s{" "}
    <Link target="_blank" href="https://www.attend.tech/terms-of-use">
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link target="_blank" href="https://www.attend.tech/privacy-policy">
      Privacy Policy
    </Link>
    .
  </>
);

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      {/* Google Ads — universal tag + purchase conversion */}
      <script
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-google-ads-loader`}
        src="https://www.googletagmanager.com/gtag/js?id=AW-942442163"
      />

      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        id={`${TEAM_SCHEMA}-google-ads-inline`}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'AW-942442163');

            window.addEventListener("${constants.customEvents.purchaseSuccess}", function (e) {
              try {
                const { cartRes, checkoutRes } = e.detail;

                gtag('event', 'conversion', {
                  'send_to': 'AW-942442163/_vEmCNCxj6gBELONssED',
                  'value': cartRes.totalAmount,
                  'currency': 'USD',
                  'transaction_id': checkoutRes.orderNumber,
                });

                console.log('google ads conversion event fired.');
              } catch(error) {
                console.log('google ads conversion event error:', error);
              }
            });
          `,
        }}
      />

      {/* Meta — universal pixel + purchase conversion */}
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-meta-pixel`}
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '2130350600536400');
            fbq('track', 'PageView');

            window.addEventListener("${constants.customEvents.purchaseSuccess}", function (e) {
              try {
                const { cartRes, checkoutRes, passes } = e.detail;

                let content_name = "";
                if (cartRes.products && cartRes.products.length) {
                  content_name = "${TEAM_SCHEMA}_single_game_purchases";
                };

                if (cartRes.passes && cartRes.passes.length && passes.length) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_name = passDetail.passTitle;
                };

                fbq('track', 'Purchase', {
                  content_name: content_name,
                  value: cartRes.totalAmount,
                  currency: 'USD',
                  order_id: checkoutRes.orderNumber,
                });

                console.log('meta pixel purchase event fired.');
              } catch(error) {
                console.log('meta pixel purchase event error:', error);
              }
            });
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=2130350600536400&ev=PageView&noscript=1"
        />
      </noscript>

      {/* TikTok — universal pixel + purchase conversion */}
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-tiktok-pixel`}
        dangerouslySetInnerHTML={{
          __html: `
            !function (w, d, t) {
              w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
              var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
              ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};

              ttq.load('CKTULMRC77UAIQIC6P50');
              ttq.page();
              console.log('tiktok pixel page view fired.');
            }(window, document, 'ttq');

            window.addEventListener("${constants.customEvents.purchaseSuccess}", function (e) {
              try {
                const { cartRes, checkoutRes, passes, user } = e.detail;

                // Advanced matching. Guarded on its own so a missing or malformed
                // profile costs attribution quality, never the conversion itself.
                try {
                  if (user && (user.email || user.phoneNumber)) {
                    const digits = String(user.phoneNumber || '').replace(/\\D/g, '');
                    const phoneNumber = digits
                      ? (digits.length === 11 && digits.charAt(0) === '1' ? '+' + digits : '+1' + digits)
                      : undefined;

                    ttq.identify({
                      email: user.email,
                      phone_number: phoneNumber,
                    });
                  }
                } catch(error) {
                  console.log('tiktok pixel identify error:', error);
                }

                const contents = [];

                if (cartRes.products && cartRes.products.length) {
                  for (const product of cartRes.products) {
                    contents.push({
                      content_id: String(product.eventCode),
                      content_type: 'product',
                      content_name: product.productTitle,
                      quantity: product.seatCount,
                      price: product.price,
                    });
                  }
                };

                if (cartRes.passes && cartRes.passes.length && passes.length) {
                  for (const cartPass of cartRes.passes) {
                    const passDetail = passes.find((pass) => pass.passId == cartPass.passId);
                    if (!passDetail) continue;
                    contents.push({
                      content_id: String(passDetail.eventCode),
                      content_type: 'product',
                      content_name: passDetail.passTitle,
                      quantity: cartPass.seatCount,
                      price: passDetail.price,
                    });
                  }
                };

                ttq.track('Purchase', {
                  contents: contents,
                  value: cartRes.totalAmount,
                  currency: 'USD',
                  order_id: checkoutRes.orderNumber,
                });

                console.log('tiktok pixel purchase event fired.');
              } catch(error) {
                console.log('tiktok pixel purchase event error:', error);
              }
            });
          `,
        }}
      />
    </Head>
  );
};
